import React, { useEffect, useState } from "react";
import { Text } from "@shoptet/ui";
import cs from "classnames";
import { useTranslation } from "react-i18next";
import { CurrencyT } from "../../graphql/generated/graphql";
import { formatCurrencyUnit, formatMoney } from "../../i18n/formatNumbers";
import NumberFieldWithUnit from "../NumberFieldWithUnit";
import { roundAmount } from "./useCreditPayment";

export enum CreditTypes {
  CUSTOM = "CUSTOM",
  ONE_MONTH = "ONE_MONTH",
  PROCEED_WITHOUT_ADDING = "PROCEED_WITHOUT_ADDING",
  THREE_MONTHS = "THREE_MONTHS",
}

type CreditOptionsPropsT = {
  currency: CurrencyT;
  initialCreditType?: CreditTypes;
  initialCustomAmount?: number;
  isFirstPayment: boolean;
  maximalPaymentAmount: number;
  minimalNextPaymentAmount: number;
  monthlyBudget: number;
  monthlyBudgetThreeMonths: number;
  onCreditChange: (creditType: CreditTypes, creditAmount: number, isValid: boolean) => void;
  showProceedWithoutAdding?: boolean;
};

export const CreditOptions = ({
  currency,
  initialCreditType = CreditTypes.THREE_MONTHS,
  initialCustomAmount,
  maximalPaymentAmount,
  minimalNextPaymentAmount,
  monthlyBudget,
  monthlyBudgetThreeMonths,
  onCreditChange,
  showProceedWithoutAdding = false,
}: CreditOptionsPropsT) => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.Credits" });

  const [creditType, setCreditType] = useState<CreditTypes>(initialCreditType);
  const [creditPartToTop, setCreditPartToTop] = useState<number>(0);
  const [customCreditPartToTop, setCustomCreditPartToTop] = useState<number | string>(initialCustomAmount ?? "");

  const customCreditPartToTopString = customCreditPartToTop.toString();
  const customCreditAmount = parseFloat(customCreditPartToTopString === "" ? "0" : customCreditPartToTop.toString());

  const underLimit = customCreditAmount < minimalNextPaymentAmount;
  const overLimit = customCreditAmount > maximalPaymentAmount;

  useEffect(() => {
    if (monthlyBudgetThreeMonths) {
      setCreditPartToTop(monthlyBudgetThreeMonths);
    }
  }, [monthlyBudgetThreeMonths]);

  useEffect(() => {
    const amountToTop = creditType === CreditTypes.CUSTOM ? customCreditAmount : creditPartToTop;
    const isCustomCreditValid = !(underLimit || overLimit);
    const isValid = creditType === CreditTypes.CUSTOM ? isCustomCreditValid : true;
    const finalAmount = creditType === CreditTypes.PROCEED_WITHOUT_ADDING ? 0 : amountToTop;
    onCreditChange(creditType, finalAmount, isValid);
  }, [creditType, creditPartToTop, customCreditAmount, underLimit, overLimit, onCreditChange]);

  const selectCreditOptionHandler = (type: CreditTypes) => {
    setCreditType(type);
    setCustomCreditPartToTop("");
    switch (type) {
      case CreditTypes.ONE_MONTH:
        if (monthlyBudget) {
          setCreditPartToTop(monthlyBudget);
        }
        break;
      case CreditTypes.THREE_MONTHS:
        if (monthlyBudgetThreeMonths) {
          setCreditPartToTop(monthlyBudgetThreeMonths);
        }
        break;
      case CreditTypes.CUSTOM:
        setCreditPartToTop(0);
        break;
      case CreditTypes.PROCEED_WITHOUT_ADDING:
        setCreditPartToTop(0);
        break;
      default:
        break;
    }
  };

  const updateCustomCreditPartToTop = (value: number | null) => {
    // an emptied field stays empty, so the minimum-amount message keeps waiting for a real value
    if (value === null) {
      setCustomCreditPartToTop("");
      return;
    }

    setCustomCreditPartToTop(value < 0 ? 0 : value);
  };

  let customCreditValidationMessage = "";
  if (underLimit) {
    customCreditValidationMessage = t("under limit", {
      amount: formatMoney({ amount: minimalNextPaymentAmount, currency }),
    });
  }
  if (overLimit) {
    customCreditValidationMessage = t("over limit", {
      amount: formatMoney({ amount: maximalPaymentAmount, currency }),
    });
  }

  return (
    <>
      <div
        className={cs("credit-top-option", { active: creditType === CreditTypes.ONE_MONTH })}
        data-test-id="credit-increase-30days-option"
        onClick={() => selectCreditOptionHandler(CreditTypes.ONE_MONTH)}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <div className="radio-indicator" />
          <div style={{ maxWidth: "350px" }}>
            <div style={{ marginBottom: "4px" }}>
              <Text appearance="strong-lg">{t("Campaigns for 1 month")}</Text>
            </div>
            <Text>
              {t("A basic time to get started. During this time, the campaign will learn to target your ad.")}
            </Text>
          </div>
        </div>
        <div>
          <div style={{ marginBottom: "4px" }}>
            <Text appearance="strong-lg">
              {formatMoney({
                amount: roundAmount(monthlyBudget),
                currency,
              })}
            </Text>
          </div>
          <div style={{ textAlign: "right" }}>
            <Text>{t("One month")}</Text>
          </div>
        </div>
      </div>
      <div
        className={cs("credit-top-option", { active: creditType === CreditTypes.THREE_MONTHS })}
        data-test-id="credit-increase-90days-option"
        onClick={() => selectCreditOptionHandler(CreditTypes.THREE_MONTHS)}
      >
        <div className="recommended-label">{t("Recommended")}</div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div className="radio-indicator" />
          <div style={{ maxWidth: "350px" }}>
            <div style={{ marginBottom: "4px" }}>
              <Text appearance="strong-lg">{t("Campaigns for 3 months")}</Text>
            </div>
            <Text>{t("Optimal for better results.")}</Text>
          </div>
        </div>
        <div>
          <div style={{ marginBottom: "4px" }}>
            <Text appearance="strong-lg">
              {formatMoney({
                amount: roundAmount(monthlyBudgetThreeMonths),
                currency,
              })}
            </Text>
          </div>
          <div style={{ textAlign: "right" }}>
            <Text>{t("Three months")}</Text>
          </div>
        </div>
      </div>
      <div
        className={cs("credit-top-option", { active: creditType === CreditTypes.CUSTOM })}
        data-test-id="credit-increase-custom-option"
        onClick={() => selectCreditOptionHandler(CreditTypes.CUSTOM)}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <div className="radio-indicator" />
          <div style={{ maxWidth: "350px" }}>
            <div style={{ marginBottom: "4px" }}>
              <Text appearance="strong-lg">{t("Own amount lowercase")}</Text>
            </div>
            <Text>
              {t("The minimum amount is {{ minimalAmount }}", {
                minimalAmount: formatMoney({ amount: minimalNextPaymentAmount, currency }),
              })}
            </Text>
          </div>
        </div>
        <div>
          <NumberFieldWithUnit
            label={t("Own amount lowercase")}
            name="credit_amount"
            unit={formatCurrencyUnit(currency)}
            value={customCreditPartToTopString === "" ? null : Number(customCreditPartToTop)}
            labelHidden
            onChange={updateCustomCreditPartToTop}
          />
          {customCreditValidationMessage && customCreditPartToTopString !== "" && (
            <div style={{ maxWidth: "220px", color: "var(--app-critical)" }}>{customCreditValidationMessage}</div>
          )}
        </div>
      </div>
      {showProceedWithoutAdding && (
        <div
          className={cs("credit-top-option", { active: creditType === CreditTypes.PROCEED_WITHOUT_ADDING })}
          data-test-id="credit-proceed-without-adding-option"
          onClick={() => selectCreditOptionHandler(CreditTypes.PROCEED_WITHOUT_ADDING)}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <div className="radio-indicator" />
            <div style={{ maxWidth: "350px" }}>
              <div>
                <Text>{t("Continue without topping")}</Text>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
